import { closeInteraction, getAvailableResources, submitTransaction } from "@/services/encompass.service";
import { TransactionTypeEnum, AuditState, AuditDetail } from "@/types/epc-interfaces";
import { getSvg } from "@/utils/getSvgs";
import { useEllimae } from "@/store/useEllimae";
import React from "react";
import { Button } from "@/components/ui/button";
import { Divider } from "@/components/ui/divider";
import { LoanLightTopBarWrapper } from "@/components/legacy-wrappers/LoanLightTopBarWrapper";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { logAction, updateSessionContext } from "@/services/datadog.service";
export const NewAudit = () => {
  const ellimaeState = useEllimae(state => state);
  const documentsCount = ellimaeState.resources ? ellimaeState.resources.length : 0;
  const [auditIsLoading, setAuditIsLoading] = React.useState(false);
  const handleRemoveDocument = (indexToRemove: number) => {
    ellimaeState.setResources((ellimaeState.resources || []).filter((_, i) => i !== indexToRemove));
  };
  const handleClose = () => {
    (async () => {
      await closeInteraction();
    })().catch(e => {
      console.error(e);
    });
  };
  const handleAddDocument = async () => {
    try {
      const resources = await getAvailableResources();
      console.log("[NewAudit] Fetched resources:", resources);
      ellimaeState.setResources([...(ellimaeState.resources || []), ...resources]);
    } catch (e_0) {
      console.error(e_0);
    }
  };
  const handleRunAudit = async () => {
    setAuditIsLoading(true);
    try {
      console.log({
        directTansactionId: ellimaeState.transactionId,
        fromAuditDetails: ellimaeState.originResponse?.auditDetail?.transactionStateId
      });
      const transactionId = await submitTransaction({
        requestType: TransactionTypeEnum.AUS,
        resources: ellimaeState.resources || [],
        transactionId: ellimaeState.transactionId ?? ellimaeState.originResponse?.auditDetail?.transactionStateId ?? null
      });

      // Immediately update state with transactionId from submitTransaction response
      // This ensures it's available even if the callback hasn't processed yet
      if (transactionId) {
        ellimaeState.setEllimaeState({
          transactionId
        });
      }
      console.log("[NewAudit] Audit transaction submitted, ID:", transactionId);

      // Update session context with new transactionId
      // Note: auditRunId and transactionStateId will be updated when backend processes the audit
      updateSessionContext({
        sessionId: ellimaeState.sessionId || null,
        loanId: ellimaeState.originResponse?.encompassLoanId || null,
        transactionId,
        transactionStateId: ellimaeState.originResponse?.auditDetail?.transactionStateId || null,
        auditRunId: ellimaeState.originResponse?.auditDetail?.id || null
      });

      // Track audit request in Datadog with all identifiers for correlation
      logAction("audit_requested", {
        sessionId: ellimaeState.sessionId || null,
        transactionId,
        loanId: ellimaeState.originResponse?.encompassLoanId || null,
        clientId: ellimaeState.originResponse?.clientId || null,
        transactionStateId: ellimaeState.originResponse?.auditDetail?.transactionStateId || null,
        auditRunId: ellimaeState.originResponse?.auditDetail?.id || null,
        isUat: ellimaeState.isUat || null,
        documentCount: ellimaeState.resources?.length || 0,
        isNewAudit: !ellimaeState.transactionId
      });
      console.log("[NewAudit] Updating audit detail to Processing state");
      const updatedState = {
        ...ellimaeState.originResponse?.auditDetail,
        auditState: AuditState.Processing,
        errorMessage: null,
        errorCode: null,
        errorStep: null,
        errorTimestamp: null
      } as AuditDetail; //TODO: Fix type assertion

      ellimaeState.setEllimaeState({
        originResponse: ellimaeState.originResponse ? {
          ...ellimaeState.originResponse,
          auditDetail: updatedState
        } : null
      });
    } catch (e_1) {
      console.error(e_1);
      setAuditIsLoading(false);
    }
  };
  return <div className="min-h-screen block bg-brand-900">
      <LoanLightTopBarWrapper className="sticky top-0">
        <div slot="right">
          <Button color="dark" onClick={handleClose} className="min-h-8 px-3 py-[5.5px] items-center">
            <span dangerouslySetInnerHTML={{
            __html: getSvg("xSvg")
          }} />
          </Button>
        </div>
      </LoanLightTopBarWrapper>

      <div className="mt-9 mx-auto bg-white rounded-2xl p-8 flex flex-col items-center justify-center gap-4 w-148">
        <span dangerouslySetInnerHTML={{
        __html: getSvg("loanLightIconSvg")
      }} />
        <h2 className="text-[32px] font-medium text-zinc-900 leading-none tracking-[-0.96px] m-0">
          Run Audit
        </h2>
        <p className="text-base font-normal text-(--accent\/950,#03101c) leading-normal tracking-[-0.32px] m-0 text-center">
          LoanLight will analyze the loan file for completeness and consistency.
        </p>
        <ol className="w-full text-center text-base leading-normal text-(--accent\/950,#03101c) space-y-1 list-decimal list-inside">
          <li>
            Click <strong>Add documents</strong> to bring up eFolder
          </li>
          <li>Select all the documents to analyze</li>
          <li>
            Click the <strong>Upload</strong> button at the top right
          </li>
          <li>Wait for all docs to show a &quot;Complete&quot; status</li>
          <li>
            Then click the <strong>Done</strong> button at the bottom right
          </li>
        </ol>
        <p className="w-full text-center text-xs italic text-(--accent\/950,#03101c) leading-normal">
          You can always come back and add more documents later.
        </p>
        <Divider />
        <div className={`flex gap-3 w-full ${documentsCount > 0 ? "py-4" : "pt-4"}`}>
          <Button color="light" className="w-full " onClick={handleAddDocument} disabled={!ellimaeState.originResponse?.originData?.id || auditIsLoading}>
            {documentsCount === 0 ? "Add Documents" : "Add More Documents"}
          </Button>
          <Button color="dark" className="w-full items-center" onClick={handleRunAudit} disabled={!ellimaeState.originResponse?.originData?.id || documentsCount === 0 || auditIsLoading} title={documentsCount === 0 ? "Please add at least one document before running an audit" : undefined}>
            {auditIsLoading ? <span dangerouslySetInnerHTML={{
            __html: getSvg("spinningDotsSvg")
          }} /> : null}
            Run Audit
          </Button>
        </div>
        {documentsCount > 0 && <div className="w-full">
            <Divider />
            <p className="px-8 py-5 text-xl font-medium text-brand-950 tracking-[-0.6px] leading-tight">
              Selected Documents
            </p>
            <ul className="w-full">
              {(ellimaeState.resources || []).map((resource, index) => <li key={index} className="flex gap-2.5 h-9 items-center px-8">
                  <span className="flex-1 text-base text-brand-950 leading-normal tracking-[-0.32px] truncate">
                    {resource.name ?? resource.id}
                  </span>
                  <Button plain onClick={() => handleRemoveDocument(index)} className="flex items-center justify-center shrink-0 text-brand-950 hover:opacity-70" aria-label="Remove document">
                    <XMarkIcon />
                  </Button>
                </li>)}
            </ul>
          </div>}
      </div>
    </div>;
};